/* eslint-disable import/no-cycle */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Badge,
  Button,
  Divider,
  Loader,
  NumberFormatter,
  Select,
  Skeleton,
  TextInput,
  Tooltip
} from '@mantine/core';
import { Lock, Minus, ShoppingCart } from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import AppStack from '../../common/AppStack';
import ResponsiveWebstoreDrawer from './ResponsiveWebstoreDrawer';
import { Context as HelperContext } from '../../../providers/HelperContextProvider';
import { Context as StoreUserContext } from '../../../providers/StoreUserContextProvider';
import {
  useCartController,
  useCartLineItemDetails
} from '../../../helpers/checkout/useCartController';
import CartDrawerLineItem from './CartDrawerLineItem';
import { triggerNotification } from '../../../helpers/notification';
import WsUnavailableProductsAlert from './sections/checkoutContent/WsUnavailableProductsAlert';
import WsFinalSaleWarning from './blocks/finalSaleWarning/WsFinalSaleWarning';

const CartDrawer = ({
  themeConfig,
  onNavigate,
  baseStoreUrl,
  ecomStoreTheme,
  mediaQueries,
  editModeEnabled,
  pathname
}) => {
  const {
    isGuest,
    ecomStoreCart,
    isCartFetching,
    checkoutStoreCart,
    removeStoreProducts,
    removeUnavailableStoreCartProducts,
    updateStoreCartProducts,
    onShowCartProductCustomization,
    getGuestCartStorage,
    setFundraiser,
    setCartEmail
  } = useCartController();
  const { lineItems: checkoutLineItemsInfo } = useCartLineItemDetails(
    ecomStoreCart
  );
  const {
    state: helperState,
    toggleWebstoreCheckoutSummaryDrawer
  } = useContext(HelperContext);
  const { state, fetchEcomStoreRosterPlayers } = useContext(StoreUserContext);
  const [checkoutState, setCheckoutState] = useState({
    email: '',
    fundraiser: {
      fkEcomStoreRosterPlayer: null,
      customInput: null
    },
    loading: false,
    removing: false,
    updating: false
  });
  const { isLargeMobileOrSmaller } = mediaQueries;
  const loading = isCartFetching || !ecomStoreCart;
  const productCount =
    ecomStoreCart?.checkoutCart.lineItems.reduce((r, c) => r + c.quantity, 0) ??
    0;
  const cartChanging = checkoutState.updating || checkoutState.removing;
  const ecomStoreRosterPlayers = state.ecomStoreRosterPlayers.value;
  const makeFundraiserInputRequired =
    themeConfig?.makeFundraiserInputRequired === 'true';
  const enableCustomFundraiser = themeConfig?.enableCustomFundraiser === 'true';

  const hasFinalSaleProducts = ecomStoreCart?.ecomStoreCartProducts?.some(
    (p) =>
      p.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct
        .ecomVendorProductExternalIds.length > 0
  );

  const checkoutButtonDisabled =
    !!ecomStoreCart?.latestVersionEcomStoreCartProducts ||
    cartChanging ||
    ecomStoreCart?.checkoutCart.lineItems.length === 0 ||
    checkoutLineItemsInfo.some(
      (i) =>
        i.isUnavailable ||
        i.requiresProductUpdate ||
        i.requiresPrintImagesUpdate
    );

  useEffect(() => {
    if (ecomStoreCart && helperState.webstore.checkoutSummaryDrawer.isOpen) {
      const guestStorageCart = getGuestCartStorage(ecomStoreCart.fkEcomStore);
      setCheckoutState({
        ...checkoutState,
        email: guestStorageCart?.email || '',
        fundraiser: guestStorageCart.fundraiser || {
          fkEcomStoreRosterPlayer: null,
          customInput: null
        }
      });
    }
  }, [ecomStoreCart, helperState.webstore.checkoutSummaryDrawer.isOpen]);

  useEffect(() => {
    if (
      helperState.webstore.checkoutSummaryDrawer.isOpen &&
      !state.ecomStoreRosterPlayers.hasFetched
    ) {
      fetchEcomStoreRosterPlayers(ecomStoreTheme.ecomStore.pkEcomStore);
    }
  }, [helperState.webstore.checkoutSummaryDrawer.isOpen]);

  useEffect(() => {
    // eslint-disable-next-line no-use-before-define
    onClose();
  }, [pathname]);

  const onClose = () => {
    toggleWebstoreCheckoutSummaryDrawer({ isOpen: false });
  };

  const submitCheckout = () => {
    setCheckoutState({
      ...checkoutState,
      loading: true
    });
    checkoutStoreCart(
      ecomStoreTheme.ecomStore.pkEcomStore,
      {
        email: checkoutState.email,
        fkEcomStoreRosterPlayer:
          checkoutState.fundraiser.fkEcomStoreRosterPlayer,
        fundraiserCustomInput: enableCustomFundraiser
          ? checkoutState.fundraiser.customInput
          : null
      },
      ({ sessionUrl }) => {
        window.location.href = sessionUrl;
      },
      (error) => {
        triggerNotification(error);
        setCheckoutState({
          ...checkoutState,
          loading: false
        });
      }
    );
  };

  const onRemoveCartProducts = (
    { pkEcomStoreCartProducts, pkEcomStoreCartPackages },
    onSuccessCallback,
    onErrorCallback
  ) => {
    setCheckoutState({
      ...checkoutState,
      removing: true
    });
    removeStoreProducts(
      ecomStoreTheme.ecomStore.pkEcomStore,
      { pkEcomStoreCartProducts, pkEcomStoreCartPackages },
      (data) => {
        setCheckoutState({
          ...checkoutState,
          removing: false
        });
        if (onSuccessCallback) {
          onSuccessCallback(data);
        }
      },
      (error) => {
        triggerNotification(error);
        setCheckoutState({
          ...checkoutState,
          removing: false
        });
        if (onErrorCallback) {
          onErrorCallback(error);
        }
      }
    );
  };

  const onUpdateProducts = (
    { ecomStoreCartProducts, ecomStoreCartPackages },
    onSuccessCallback,
    onErrorCallback
  ) => {
    setCheckoutState({
      ...checkoutState,
      updating: true
    });
    updateStoreCartProducts(
      ecomStoreTheme.ecomStore.pkEcomStore,
      {
        ecomStoreCartProducts,
        ecomStoreCartPackages
      },
      (data) => {
        setCheckoutState({
          ...checkoutState,
          updating: false
        });
        if (onSuccessCallback) {
          onSuccessCallback(data);
        }
      },
      (error) => {
        triggerNotification(error);
        setCheckoutState({
          ...checkoutState,
          updating: false
        });
        if (onErrorCallback) {
          onErrorCallback(error);
        }
      }
    );
  };

  const onRemoveUnavailableProducts = () => {
    setCheckoutState({
      ...checkoutState,
      updating: true
    });
    removeUnavailableStoreCartProducts(
      ecomStoreTheme.ecomStore.pkEcomStore,
      {},
      () => {
        setCheckoutState({
          ...checkoutState,
          removing: false
        });
        triggerNotification('Cart updated!', 'success');
      },
      (error) => {
        triggerNotification(error);
        setCheckoutState({
          ...checkoutState,
          removing: false
        });
      }
    );
  };

  const onFundraiserChange = ({ fkEcomStoreRosterPlayer, customInput }) => {
    setCheckoutState({
      ...checkoutState,
      fundraiser: {
        fkEcomStoreRosterPlayer,
        customInput
      }
    });
    setFundraiser(ecomStoreTheme.ecomStore.pkEcomStore, {
      fkEcomStoreRosterPlayer,
      customInput
    });
  };

  return (
    <ResponsiveWebstoreDrawer
      closeIcon={
        <Tooltip label="Close" position="bottom" withArrow>
          <AppStack>
            <Minus size={18} />
          </AppStack>
        </Tooltip>
      }
      editModeEnabled={editModeEnabled}
      footerContent={
        loading ? (
          <AppStack
            style={{
              padding: 16,
              gap: 16,
              backgroundColor: '#000',
              color: '#FFF',
              opacity: 0.8
            }}
          >
            <AppFlexbox
              style={{
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Skeleton height={24} width={80} />
              <Skeleton height={24} width={60} />
            </AppFlexbox>
            <AppFlexbox
              style={{
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Skeleton height={24} width={80} />
              <Skeleton height={24} width={60} />
            </AppFlexbox>
            <AppFlexbox
              style={{
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Skeleton height={24} width={80} />
              <Skeleton height={24} width={60} />
            </AppFlexbox>
            <Skeleton height={48} width="100%" />
            <Divider
              color="#FFF"
              labelPosition="center"
              style={{ margin: '5px 0px' }}
            />
            <AppStack style={{ gap: 10 }}>
              <Skeleton height={48} width="100%" />
              <AppFlexbox style={{ justifyContent: 'center' }}>
                <Skeleton height={20} width="80%" />
              </AppFlexbox>
            </AppStack>
          </AppStack>
        ) : (
          <AppStack
            style={{
              padding: 16,
              gap: 16,
              backgroundColor: '#000',
              color: '#FFF',
              opacity: 0.8
            }}
          >
            <AppStack style={{ gap: 10 }}>
              {ecomStoreCart?.checkoutCart.subtotalInCents !==
                ecomStoreCart.checkoutCart.totalInCents && (
                <AppFlexbox
                  style={{
                    justifyContent: 'space-between',
                    flexWrap: 'nowrap'
                  }}
                >
                  <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                    Subtotal
                  </AppText>
                  {cartChanging ? (
                    <Skeleton height={24} width={60} />
                  ) : (
                    <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                      <NumberFormatter
                        decimalScale={2}
                        displayType="text"
                        fixedDecimalScale
                        prefix="$"
                        thousandSeparator
                        value={ecomStoreCart.checkoutCart.subtotalInCents / 100}
                      />
                    </AppText>
                  )}
                </AppFlexbox>
              )}

              {!cartChanging &&
                ecomStoreCart?.checkoutCart.discountTotalInCents > 0 && (
                  <AppFlexbox
                    style={{
                      justifyContent: 'space-between',
                      flexWrap: 'nowrap'
                    }}
                  >
                    <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                      Discount
                    </AppText>
                    <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                      <NumberFormatter
                        decimalScale={2}
                        displayType="text"
                        fixedDecimalScale
                        prefix="$"
                        thousandSeparator
                        value={
                          (ecomStoreCart.checkoutCart.discountTotalInCents /
                            100) *
                          -1
                        }
                      />
                    </AppText>
                  </AppFlexbox>
                )}
              {!cartChanging &&
                ecomStoreCart?.checkoutCart.taxTotalInCents > 0 && (
                  <AppFlexbox
                    style={{
                      justifyContent: 'space-between',
                      flexWrap: 'nowrap'
                    }}
                  >
                    <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                      Taxes
                    </AppText>
                    <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                      <NumberFormatter
                        decimalScale={2}
                        displayType="text"
                        fixedDecimalScale
                        prefix="$"
                        thousandSeparator
                        value={ecomStoreCart.checkoutCart.taxTotalInCents / 100}
                      />
                    </AppText>
                  </AppFlexbox>
                )}
              {!cartChanging &&
                ecomStoreCart?.checkoutCart.customerServiceFeeTotalInCents >
                  0 && (
                  <AppFlexbox
                    style={{
                      justifyContent: 'space-between',
                      flexWrap: 'nowrap'
                    }}
                  >
                    <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                      Service Fee
                    </AppText>
                    <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                      <NumberFormatter
                        decimalScale={2}
                        displayType="text"
                        fixedDecimalScale
                        prefix="$"
                        thousandSeparator
                        value={
                          ecomStoreCart.checkoutCart
                            .customerServiceFeeTotalInCents / 100
                        }
                      />
                    </AppText>
                  </AppFlexbox>
                )}

              {ecomStoreCart?.checkoutCart.tieredPricingFeeInCents > 0 && (
                <AppFlexbox
                  style={{
                    justifyContent: 'space-between',
                    flexWrap: 'nowrap'
                  }}
                >
                  <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                    Service Fee
                  </AppText>
                  {cartChanging ? (
                    <Skeleton height={24} width={60} />
                  ) : (
                    <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                      <NumberFormatter
                        decimalScale={2}
                        displayType="text"
                        fixedDecimalScale
                        prefix="$"
                        thousandSeparator
                        value={
                          ecomStoreCart.checkoutCart.tieredPricingFeeInCents /
                          100
                        }
                      />
                    </AppText>
                  )}
                </AppFlexbox>
              )}

              <AppFlexbox
                style={{
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap',
                  alignItems: 'center'
                }}
              >
                <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                  Shipping
                </AppText>
                {cartChanging ? (
                  <Skeleton height={24} width={60} />
                ) : ecomStoreCart?.checkoutCart.freeShipping ? (
                  <AppText
                    style={{ fontSize: 20, fontWeight: 500, color: 'green' }}
                  >
                    Free
                  </AppText>
                ) : (
                  <AppText style={{ color: '#FFF', fontSize: 13 }}>
                    Continue to cart to calculate
                  </AppText>
                )}
              </AppFlexbox>

              <AppFlexbox
                style={{
                  justifyContent: 'space-between',
                  flexWrap: 'nowrap',
                  alignItems: 'center'
                }}
              >
                <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                  Total
                </AppText>
                {cartChanging ? (
                  <Skeleton height={24} width={60} />
                ) : (
                  <AppText style={{ fontSize: 20, fontWeight: 500 }}>
                    <NumberFormatter
                      decimalScale={2}
                      displayType="text"
                      fixedDecimalScale
                      prefix="$"
                      thousandSeparator
                      value={ecomStoreCart.checkoutCart.totalInCents / 100}
                    />
                  </AppText>
                )}
              </AppFlexbox>
              {isGuest && ecomStoreCart?.checkoutCart.lineItems.length > 0 && (
                <TextInput
                  autoComplete="on"
                  disabled={cartChanging || loading || checkoutState.loading}
                  onChange={(e) => {
                    setCheckoutState({
                      ...checkoutState,
                      email: e.currentTarget.value
                    });
                    setCartEmail(
                      ecomStoreTheme.ecomStore.pkEcomStore,
                      e.currentTarget.value
                    );
                  }}
                  placeholder="Email address"
                  required
                  type="email"
                  value={checkoutState.email}
                />
              )}
              <Button
                id="checkout-order-summary-submit-drawer"
                style={{ display: 'none' }}
                type="submit"
              />

              {hasFinalSaleProducts && (
                <WsFinalSaleWarning
                  editModeEnabled={editModeEnabled}
                  isCheckout
                  mediaQueries={mediaQueries}
                />
              )}

              {ecomStoreCart?.checkoutCart.freeShipping ? (
                <Button
                  color="blue"
                  disabled={checkoutButtonDisabled}
                  leftSection={<Lock />}
                  loading={checkoutState.loading}
                  onClick={() => {
                    document
                      .getElementById('checkout-order-summary-submit-drawer')
                      .click();
                  }}
                  size="lg"
                  type="button"
                  variant="filled"
                >
                  Continue to Checkout
                </Button>
              ) : (
                <Button
                  color="blue"
                  component={Link}
                  leftSection={<ShoppingCart />}
                  loading={checkoutState.loading}
                  onClick={onNavigate}
                  size="lg"
                  to={`${baseStoreUrl}/checkout/cart`}
                  type="button"
                  variant="filled"
                >
                  Continue to Cart
                </Button>
              )}
            </AppStack>
          </AppStack>
        )
      }
      formSectionProps={{
        hideButtons: true,
        onSubmit: () => {
          submitCheckout();
        }
      }}
      mediaQueries={mediaQueries}
      onClose={onClose}
      opened={helperState.webstore.checkoutSummaryDrawer.isOpen}
      position="right"
      size={isLargeMobileOrSmaller ? '100%' : 450}
      styles={{}}
      title={
        <AppFlexbox style={{ alignItems: 'center', gap: 10 }}>
          <AppText style={{ fontSize: 28, fontWeight: 500 }}>Cart</AppText>
          {checkoutState.updating || loading ? (
            <Loader color="blue" size={28} />
          ) : (
            <Badge circle color="blue" radius="xl" size={28} variant="filled">
              <AppText style={{ fontSize: 18, fontWeight: 500 }}>
                {productCount}
              </AppText>
            </Badge>
          )}
        </AppFlexbox>
      }
    >
      {!loading && (
        <WsUnavailableProductsAlert
          baseStoreUrl={baseStoreUrl}
          ecomStoreCart={ecomStoreCart}
          lineItemsInfo={checkoutLineItemsInfo}
          mediaQueries={mediaQueries}
          onNavigate={onNavigate}
          onRemoveUnavailableProducts={onRemoveUnavailableProducts}
          submitting={checkoutState.loading || checkoutState.loadingCheckout}
        />
      )}

      <AppStack
        style={{
          flex: 1,
          gap: 16,
          flexDirection: 'column',
          height: '100%',
          padding: 16
        }}
      >
        {ecomStoreCart &&
          ecomStoreCart.checkoutCart.lineItems.length > 0 &&
          ecomStoreRosterPlayers.length > 0 && (
            <>
              <Select
                clearable
                data={ecomStoreRosterPlayers.map((p) => ({
                  label: p.name,
                  value: p.pkEcomStoreRosterPlayer.toString()
                }))}
                description="Select the player that you are fundraising for"
                disabled={cartChanging || loading || checkoutState.loading}
                label="Fundraiser"
                onChange={(fkEcomStoreRosterPlayer) => {
                  onFundraiserChange({
                    ...checkoutState.fundraiser,
                    fkEcomStoreRosterPlayer
                  });
                }}
                required={makeFundraiserInputRequired}
                searchable
                size="sm"
                styles={{
                  input: {
                    minHeight: 0,
                    height: 36,
                    fontSize: 14
                  }
                }}
                value={checkoutState.fundraiser.fkEcomStoreRosterPlayer || null}
              />
              {enableCustomFundraiser && (
                <TextInput
                  disabled={cartChanging || loading || checkoutState.loading}
                  label="Player Name"
                  onChange={(e) => {
                    onFundraiserChange({
                      ...checkoutState.fundraiser,
                      customInput: e.currentTarget.value?.substring(0, 255)
                    });
                  }}
                  required={makeFundraiserInputRequired}
                  size="sm"
                  styles={{
                    input: {
                      minHeight: 0,
                      height: 36,
                      fontSize: 14
                    }
                  }}
                  value={checkoutState.fundraiser?.customInput || ''}
                />
              )}
              <Divider />
            </>
          )}
        {!loading &&
          ecomStoreCart.checkoutCart.lineItems
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .map((lineItem) => (
              <React.Fragment key={lineItem.entityId}>
                <CartDrawerLineItem
                  key={lineItem.entityId}
                  disabled={checkoutState.loading || cartChanging || loading}
                  ecomStoreCart={ecomStoreCart}
                  lineItem={lineItem}
                  lineItemInfo={checkoutLineItemsInfo.find(
                    (p) => p.entityId === lineItem.entityId
                  )}
                  onCustomize={() => {
                    if (lineItem.isBundleProduct) {
                      onShowCartProductCustomization({
                        ecomStoreCartPackage: ecomStoreCart.ecomStoreCartPackages.find(
                          (p) => p.pkEcomStoreCartPackage === lineItem.entityId
                        )
                      });
                    }
                    else {
                      onShowCartProductCustomization({
                        ecomStoreCartProduct: ecomStoreCart.ecomStoreCartProducts.find(
                          (p) => p.pkEcomStoreCartProduct === lineItem.entityId
                        )
                      });
                    }
                  }}
                  onNavigate={(e) => {
                    onNavigate(e);
                    onClose();
                  }}
                  onRemove={onRemoveCartProducts}
                  onRemoveUnavailableProducts={onRemoveUnavailableProducts}
                  onUpdate={onUpdateProducts}
                  productUrl={`${baseStoreUrl}/products/${lineItem?.urlHandle}`}
                  submitting={
                    checkoutState.loading || checkoutState.loadingCheckout
                  }
                />
                <Divider />
              </React.Fragment>
            ))}
      </AppStack>
    </ResponsiveWebstoreDrawer>
  );
};

CartDrawer.propTypes = {
  baseStoreUrl: PropTypes.string,
  ecomStoreTheme: PropTypes.object,
  editModeEnabled: PropTypes.bool,
  logoImageUrl: PropTypes.string,
  mediaQueries: PropTypes.object,
  onNavigate: PropTypes.func,
  pathname: PropTypes.string,
  themeConfig: PropTypes.object
};

export default CartDrawer;
